// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import { ActionSheetV2, Body, Button, Column, InputRadio, InputRadioGroup, LgPadding } from '@alto/design-system';
import { InputLabelRow } from '@alto/design-system/src/components/inputs/src/InputText/InputLabelRow';
import React from 'react';
import { useController, useForm } from 'react-hook-form';
import { type VerificationCommsChannel } from '~shared/actions/verifications';
import {
  RESEND_VERIFICATION_TITLE,
  VERIFICATION_CTA_LOADING_TEXT,
  VERIFICATION_CTA_TEXT,
  VERIFICATION_MULTIPLE_OPTIONS_BODY,
  VERIFICATION_OPTIONS_TEXT,
  VERIFICATION_TITLE,
} from '~shared/features/onboarding/constants';
import { type Option } from '~shared/features/onboarding/selectors/getOnboardingVerification';
import { getErrorMessage } from '~shared/helpers/helper';
import { type APIError } from '~shared/types';

export type Values = {
  channel: VerificationCommsChannel | null | undefined;
};

type Props = {
  readonly error?: APIError | null;
  readonly loading?: boolean;
  readonly onSubmit: (values: Values) => void;
  readonly options: Option[];
  readonly resend?: boolean;
};

export const VerificationOptionsActionSheet = ({ error, loading = false, onSubmit, options, resend }: Props) => {
  const { control, getValues, setValue } = useForm<Values>({
    defaultValues: { channel: null },
    mode: 'onBlur',
  });
  const channelController = useController({
    control,
    name: 'channel',
    rules: {
      required: 'Please select a verification method',
    },
  });

  const radioOptions = options.map((option) => {
    const { channel, contact } = option;
    const optionText = VERIFICATION_OPTIONS_TEXT(channel, contact);
    return (
      <InputRadio
        value={channel}
        key={channel}
        label={optionText}
        radioPlacement="left"
      />
    );
  });

  return (
    <ActionSheetV2
      title={resend ? RESEND_VERIFICATION_TITLE : VERIFICATION_TITLE}
      analyticsName="verification_options"
      buttons={[
        <Button
          key="submit-verification-button"
          label={VERIFICATION_CTA_TEXT}
          loading={loading}
          loadingLabel={VERIFICATION_CTA_LOADING_TEXT}
          onPress={() => {
            onSubmit(getValues());
          }}
          disabled={!channelController.field.value}
        />,
      ]}
    >
      <LgPadding>
        <Column gap="SM">
          <Body>{VERIFICATION_MULTIPLE_OPTIONS_BODY}</Body>
          <>
            <InputLabelRow
              label="Verification method"
              error={channelController.fieldState.error?.message}
              required
            />
            <InputRadioGroup
              label="Verification methods"
              onValueChange={(value) => {
                setValue('channel', value as VerificationCommsChannel);
              }}
              value={channelController.field.value}
            >
              {radioOptions}
            </InputRadioGroup>
          </>
          {!!error && <Body color={COLORS.TEXT_COLORS.DANGER}>{getErrorMessage(error)}</Body>}
        </Column>
      </LgPadding>
    </ActionSheetV2>
  );
};
